import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, redirectPath, setRedirectPath } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for session expired flag in URL
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      // Store the redirect path if provided
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        setRedirectPath(redirectTo);
      }
    }
  }, [searchParams, setRedirectPath]);

  useEffect(() => {
    if (user) {
      const path = redirectPath || '/desk/today';
      setRedirectPath(null);
      navigate(path);
    }
  }, [user, navigate, redirectPath, setRedirectPath]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back.');
      const path = redirectPath || '/desk/today';
      setRedirectPath(null);
      navigate(path);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center p-4 antialiased">
      <div className="w-full max-w-md relative z-10 animate-fade-in">
        {/* Brand mark */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-[34px] sm:text-[38px] text-stone-900 mb-2 leading-tight">Maris Desk</h1>
          <p className="text-[10.5px] tracking-[0.22em] uppercase font-medium text-[#3F617E]">
            Calm hospitality operations
          </p>
        </div>

        <Card className="border-stone-200 shadow-[0_1px_2px_rgba(0,0,0,0.03),_0_40px_80px_-40px_rgba(63,97,126,0.18)] bg-white">
          {sessionExpired && (
            <Alert variant="destructive" className="m-4 mb-0 border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Your session has expired. Please sign in again to continue.
              </AlertDescription>
            </Alert>
          )}
          <CardHeader className="space-y-1.5 pb-4 pt-7">
            <CardTitle className="font-heading text-[24px] sm:text-[26px] text-center text-stone-900 leading-tight">Existing Client Login</CardTitle>
            <CardDescription className="text-center text-stone-600 text-[13.5px]">
              Sign in to your Maris Desk team workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-7">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password" className="text-[11px] tracking-[0.08em] uppercase text-stone-500">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-[11.5px] text-[#3F617E] hover:text-[#345470] underline-offset-4 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="bg-white border-stone-300 focus-visible:ring-[#3F617E]/40 focus-visible:border-[#3F617E]"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full h-11 text-[14.5px] font-medium tracking-wide shadow-md shadow-[#3F617E]/15"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
            </form>

            {/* Sales-led onboarding: no public sign-up. Direct prospects to
                the Guest Request Flow Review intake instead. */}
            <p className="text-[12.5px] text-center text-stone-500 mt-6 leading-relaxed">
              New to Maris Desk?{' '}
              <a
                href="/request-review"
                className="text-[#3F617E] hover:text-[#345470] underline-offset-4 hover:underline font-medium"
                data-testid="request-review-link"
              >
                Request a Guest Request Flow Review
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
